import type { ImportMessage } from './importMessage';
import {
    defaultRetrieveSources,
    mergeSourcedSeries,
    type SourcedPatient,
    type SourcedSeriesRow,
    type SourceSearch,
} from './multiSourceModel';
import {
    allRemoteSeriesKeys,
    handOffAllowed,
    initialArrivals,
    summariseArrivals,
    type AcceptedSeries,
    type RemotePatientIdentity,
    type RemoteSeriesRow,
    type SeriesArrival,
} from './pacsImportModel';

/**
 * The DICOM-source side of the import dialog as one explicit state machine.
 *
 * A sibling of {@link ./importWizardMachine}, not an extension of it: the disk path reviews files the
 * browser has already read and ends by uploading them, while this path reviews remote sources' own
 * answers and ends by asking the connector to fetch them. Their stages, their evidence and their
 * terminal states differ, so folding them into one reducer would produce a state space where most
 * combinations are unreachable and the invariants of neither path are legible.
 *
 * Two invariants carry over unchanged, because they carry the safety of both paths:
 *   - every async result is tagged with the generation it was started in, and a result whose
 *     generation is no longer live is dropped before any of it is read;
 *   - a selection belongs to exactly one patient, so no edit is expressible across patients.
 *
 * One lookup runs against every included source at once and each source answers on its own, so a
 * slow or unreachable source never holds back the others. The lookup lives beside the stage rather
 * than in it: going back to the results after picking a patient costs no second query, because a
 * lookup opens one association per source and making a mis-click pay for that again is what would
 * push a user towards guessing.
 */

/** The current lookup: the term it asked about and what each included source answered. */
export type PacsLookup = {
    readonly generation: number;
    readonly term: string;
    readonly bySource: Readonly<Record<string, SourceSearch>>;
};

/** A series listing one holder of the patient returned. */
type SourceListing = {
    readonly source: string;
    readonly rows: readonly RemoteSeriesRow[];
};

/** The patient being browsed, the source it was picked from, and every source holding it. */
type PickedPatient = {
    readonly patient: RemotePatientIdentity;
    readonly pickedFrom: string;
    /** Sources listing the same person under this id, in source order. Series are read from each. */
    readonly holders: readonly string[];
};

/** A selection, and the source each selected series will be retrieved from. */
type SeriesChoice = {
    readonly rows: readonly SourcedSeriesRow[];
    readonly selected: ReadonlySet<string>;
    readonly retrieveFrom: ReadonlyMap<string, string>;
};

export type PacsStage =
    /** Looking a patient up; the lookup's per-source answers are on the state. */
    | { readonly kind: 'search' }
    | (PickedPatient & {
          readonly kind: 'loading-studies';
          readonly listed: readonly SourceListing[];
          /** Holders whose series listing failed. */
          readonly failed: readonly string[];
          readonly truncated: boolean;
      })
    | (PickedPatient &
          SeriesChoice & {
              readonly kind: 'browsing';
              readonly truncated: boolean;
              /**
               * Holders that could not list their series. What they hold is missing from the list,
               * which must say so rather than read as everything the patient has.
               */
              readonly unlisted: readonly string[];
          })
    | (PickedPatient &
          SeriesChoice & {
              readonly kind: 'requesting';
              /** Series whose retrieval the connector has accepted so far. */
              readonly accepted: number;
              /** Series whose retrieval the connector has refused so far. */
              readonly refused: number;
              /** Series whose request the connector did not answer in time. */
              readonly unanswered: number;
              /** Series the run stopped short of asking for, once a request had gone unanswered. */
              readonly notRequested: number;
              readonly total: number;
          })
    /**
     * Terminal for a retrieval nothing was accepted for. It carries no arrival and no expected
     * total: there is no acceptance to follow, so a count of instances here would describe a
     * transfer this dialog has no evidence of.
     *
     * The three tallies are not interchangeable. A refusal is the source's own answer, so nothing
     * is on its way; a request that went unanswered leaves the series' fate unknown, because the
     * connector detaches the transfer from the request before it answers, so an answer that never
     * arrived cannot say whether one is running; a series that was never requested has nothing
     * running for it at all, which is the one of the three that can be asked for again with no
     * risk of duplicating a transfer.
     */
    | (PickedPatient & {
          readonly kind: 'refused';
          readonly refused: number;
          readonly unanswered: number;
          readonly notRequested: number;
      })
    /**
     * The instances are arriving. They are pushed to the connector's own listener over a separate
     * association this dialog cannot observe, so what is shown here is not measured from the
     * transfer: it is the connector's own count of what it has stored against what it recorded as
     * expected before the transfer began. That is why this stage reports counts and never a bar or
     * a percentage, and why nothing here may imply the transfer can be stopped.
     *
     * Dismissible on purpose. The transfer outlives the dialog, and closing is how a user says they
     * do not want to be taken to the result.
     */
    | (PickedPatient & {
          readonly kind: 'arriving';
          readonly refused: number;
          readonly unanswered: number;
          readonly notRequested: number;
          readonly arrivals: readonly SeriesArrival[];
      })
    /** Terminal: every accepted series has arrived or failed, and nothing further will change. */
    | (PickedPatient & {
          readonly kind: 'arrived';
          readonly refused: number;
          readonly unanswered: number;
          readonly notRequested: number;
          readonly arrivals: readonly SeriesArrival[];
          /** The connector patient the instances landed in, when one was resolved. */
          readonly patientId?: string;
      });

export type PacsImportState = {
    /** The lifetime owning the current stage. Only the dialog's allocator mints values. */
    readonly generation: number;
    /** The sources a lookup runs against, in configuration order. */
    readonly sources: readonly string[];
    /**
     * The one lookup key. The connector matches it against the patient id exactly and against the
     * name as a prefix, and unions the two, so a user who has either in hand types it in one place.
     * The asymmetry is a safety decision: a widened id match is how a lookup offers the wrong
     * patient, while a widened name only lengthens a list the user picks from.
     */
    readonly search: string;
    readonly lookup?: PacsLookup;
    readonly stage: PacsStage;
    /** Why the last attempt did not produce a result. Never outlives the next attempt. */
    readonly error?: ImportMessage;
};

export type PacsImportAction =
    | { readonly type: 'sources-changed'; readonly sources: readonly string[] }
    | { readonly type: 'search-changed'; readonly value: string }
    | {
          readonly type: 'lookup-started';
          readonly generation: number;
          readonly term: string;
          readonly sources: readonly string[];
      }
    | { readonly type: 'lookup-cleared' }
    | {
          readonly type: 'lookup-source-settled';
          readonly generation: number;
          readonly source: string;
          readonly patients: readonly RemotePatientIdentity[];
          readonly truncated: boolean;
      }
    | {
          readonly type: 'lookup-source-failed';
          readonly generation: number;
          readonly source: string;
          readonly message: ImportMessage;
      }
    | {
          readonly type: 'patient-selected';
          readonly generation: number;
          readonly picked: SourcedPatient;
          readonly holders: readonly string[];
      }
    | {
          readonly type: 'studies-source-settled';
          readonly generation: number;
          readonly source: string;
          /** Guard: the tree answers exactly the patient id it was asked about. */
          readonly dicomPatientId: string;
          readonly rows: readonly RemoteSeriesRow[];
          readonly truncated: boolean;
      }
    | {
          readonly type: 'studies-source-failed';
          readonly generation: number;
          readonly source: string;
      }
    | { readonly type: 'series-toggled'; readonly key: string }
    | {
          readonly type: 'series-group-toggled';
          readonly keys: readonly string[];
          readonly selected: boolean;
      }
    | {
          readonly type: 'series-source-chosen';
          readonly key: string;
          readonly source: string;
      }
    | { readonly type: 'back-to-results'; readonly generation: number }
    | { readonly type: 'request-started'; readonly generation: number }
    | {
          readonly type: 'request-progress';
          readonly generation: number;
          readonly accepted: number;
          readonly refused: number;
          readonly unanswered: number;
          readonly notRequested: number;
      }
    | {
          readonly type: 'request-settled';
          readonly generation: number;
          /** The series the connector accepted, in the order they were requested. */
          readonly accepted: readonly AcceptedSeries[];
          readonly refused: number;
          readonly unanswered: number;
          readonly notRequested: number;
      }
    | {
          readonly type: 'arrivals-observed';
          readonly generation: number;
          readonly arrivals: readonly SeriesArrival[];
          /** The connector patient the instances landed in, once one exists. */
          readonly patientId?: string;
      }
    | { readonly type: 'reset'; readonly generation: number };

export function initialPacsImportState(
    generation: number,
    sources: readonly string[] = []
): PacsImportState {
    return {
        generation,
        sources,
        search: '',
        stage: { kind: 'search' },
    };
}

/**
 * A lookup needs at least one source and a term, and only runs while the results are what is on
 * screen. The minimum length applies to looking up as the user types, not to a lookup they ask for.
 */
export function canLookUp(state: PacsImportState): boolean {
    return (
        state.stage.kind === 'search' &&
        state.sources.length > 0 &&
        state.search.trim().length > 0
    );
}

/** A retrieval needs a browsed patient with at least one series still selected. */
export function canRequest(state: PacsImportState): boolean {
    return state.stage.kind === 'browsing' && state.stage.selected.size > 0;
}

export function selectedKeysOf(stage: PacsStage): ReadonlySet<string> {
    return stage.kind === 'browsing' || stage.kind === 'requesting'
        ? stage.selected
        : new Set<string>();
}

/** Whether a retrieval is in flight, which is the one stage that refuses new input. */
export function isRequesting(state: PacsImportState): boolean {
    return state.stage.kind === 'requesting';
}

/** Whether there are results to go back to from the current stage. */
export function canGoBackToResults(state: PacsImportState): boolean {
    return (
        state.lookup !== undefined &&
        state.stage.kind !== 'search' &&
        !isRequesting(state)
    );
}

/**
 * Whether a retrieval has settled with the whole of what was asked for in hand. A series the source
 * refused, one whose request went unanswered and one the run never asked for never become arrivals,
 * so the arrivals alone cannot answer this: all three tallies are their siblings on the stage and
 * have to be read beside them. An unanswered request counts against wholeness even though its
 * series may yet land, precisely because nothing here can tell which of the two happened.
 */
export function arrivedWhole(stage: PacsStage): boolean {
    return (
        stage.kind === 'arrived' &&
        stage.refused === 0 &&
        stage.unanswered === 0 &&
        stage.notRequested === 0 &&
        handOffAllowed(stage.arrivals)
    );
}

function pickedOf(stage: PickedPatient): PickedPatient {
    return {
        patient: stage.patient,
        pickedFrom: stage.pickedFrom,
        holders: stage.holders,
    };
}

/**
 * The series view once every holder has answered: merged across holders, everything selected, each
 * series retrieved from the first holder in source order. With nothing listed at all the lookup is
 * returned to with the reason.
 */
function settleStudies(
    state: PacsImportState,
    stage: Extract<PacsStage, { kind: 'loading-studies' }>
): PacsImportState {
    if (stage.listed.length + stage.failed.length < stage.holders.length) {
        return { ...state, stage };
    }
    if (stage.listed.length === 0) {
        return {
            ...state,
            stage: { kind: 'search' },
            error: { id: 'patientList.import.pacs.error.studiesFailed' },
        };
    }
    const rows = mergeSourcedSeries(stage.listed, stage.holders);
    if (rows.length === 0) {
        return {
            ...state,
            stage: { kind: 'search' },
            error: { id: 'patientList.import.pacs.error.noSeries' },
        };
    }
    return {
        ...state,
        stage: {
            kind: 'browsing',
            ...pickedOf(stage),
            rows,
            // Everything selected by default, matching the disk path: the user narrows a whole
            // patient down rather than building a selection up from nothing.
            selected: allRemoteSeriesKeys(rows),
            retrieveFrom: defaultRetrieveSources(rows),
            truncated: stage.truncated,
            unlisted: stage.failed,
        },
    };
}

export function pacsImportReducer(
    state: PacsImportState,
    action: PacsImportAction
): PacsImportState {
    switch (action.type) {
        // Excluding a source drops what it answered: results from a source the user has said not to
        // search are not something they can pick from. Including one adds nothing until it is asked.
        case 'sources-changed': {
            if (!state.lookup) {
                return { ...state, sources: action.sources };
            }
            const bySource = Object.fromEntries(
                Object.entries(state.lookup.bySource).filter(([source]) =>
                    action.sources.includes(source)
                )
            );
            return {
                ...state,
                sources: action.sources,
                lookup: { ...state.lookup, bySource },
            };
        }

        // Editing the term does not discard a result already on screen: the previous answer stays
        // readable while the next query is being typed, and only a new lookup replaces it.
        case 'search-changed':
            return { ...state, search: action.value, error: undefined };

        // Each asked source keeps its previous patients on screen, marked stale, until it answers.
        case 'lookup-started': {
            if (state.stage.kind !== 'search') {
                return state;
            }
            const previous = state.lookup?.bySource ?? {};
            const bySource: Record<string, SourceSearch> = {};
            for (const source of action.sources) {
                bySource[source] = {
                    status: 'searching',
                    patients: previous[source]?.patients ?? [],
                    truncated: previous[source]?.truncated ?? false,
                    stale: true,
                };
            }
            return {
                ...state,
                lookup: {
                    generation: action.generation,
                    term: action.term,
                    bySource,
                },
                error: undefined,
            };
        }

        case 'lookup-cleared':
            return state.stage.kind === 'search'
                ? { ...state, lookup: undefined }
                : state;

        case 'lookup-source-settled': {
            const lookup = state.lookup;
            if (!lookup || lookup.generation !== action.generation) {
                return state;
            }
            if (lookup.bySource[action.source] === undefined) {
                return state;
            }
            return {
                ...state,
                lookup: {
                    ...lookup,
                    bySource: {
                        ...lookup.bySource,
                        [action.source]: {
                            status: 'answered',
                            patients: action.patients,
                            truncated: action.truncated,
                            stale: false,
                        },
                    },
                },
            };
        }

        // One source failing takes nothing from the others; its own entry says why it has no answer.
        case 'lookup-source-failed': {
            const lookup = state.lookup;
            if (!lookup || lookup.generation !== action.generation) {
                return state;
            }
            if (lookup.bySource[action.source] === undefined) {
                return state;
            }
            return {
                ...state,
                lookup: {
                    ...lookup,
                    bySource: {
                        ...lookup.bySource,
                        [action.source]: {
                            status: 'failed',
                            patients: [],
                            truncated: false,
                            stale: false,
                            message: action.message,
                        },
                    },
                },
            };
        }

        // A single match still goes through the list rather than auto-advancing: the list is where
        // the sources' own names and birth dates are shown, and skipping it would commit a
        // retrieval for a patient whose identity the user never saw.
        case 'patient-selected': {
            if (state.stage.kind !== 'search' || action.holders.length === 0) {
                return state;
            }
            return {
                ...state,
                generation: action.generation,
                stage: {
                    kind: 'loading-studies',
                    patient: action.picked.patient,
                    pickedFrom: action.picked.source,
                    holders: action.holders,
                    listed: [],
                    failed: [],
                    truncated: false,
                },
                error: undefined,
            };
        }

        // Abandons the lifetime that was reading this patient's series, so a tree still in flight
        // cannot land on top of the list the user went back to.
        case 'back-to-results':
            if (!canGoBackToResults(state)) {
                return state;
            }
            return {
                ...state,
                generation: action.generation,
                stage: { kind: 'search' },
                error: undefined,
            };

        case 'studies-source-settled': {
            if (action.generation !== state.generation) {
                return state;
            }
            const stage = state.stage;
            if (
                stage.kind !== 'loading-studies' ||
                !stage.holders.includes(action.source)
            ) {
                return state;
            }
            // An answer about a different patient fails loudly rather than being shown: this tree is
            // what a retrieval is issued from, so mis-attributing it would fetch the wrong patient.
            if (stage.patient.dicomPatientId !== action.dicomPatientId) {
                return {
                    ...state,
                    stage: { kind: 'search' },
                    error: { id: 'patientList.import.pacs.error.wrongPatient' },
                };
            }
            return settleStudies(state, {
                ...stage,
                listed: [
                    ...stage.listed,
                    { source: action.source, rows: action.rows },
                ],
                truncated: stage.truncated || action.truncated,
            });
        }

        case 'studies-source-failed': {
            if (action.generation !== state.generation) {
                return state;
            }
            const stage = state.stage;
            if (
                stage.kind !== 'loading-studies' ||
                !stage.holders.includes(action.source)
            ) {
                return state;
            }
            return settleStudies(state, {
                ...stage,
                failed: [...stage.failed, action.source],
            });
        }

        case 'series-toggled': {
            if (state.stage.kind !== 'browsing') {
                return state;
            }
            const selected = new Set(state.stage.selected);
            if (selected.has(action.key)) {
                selected.delete(action.key);
            } else {
                selected.add(action.key);
            }
            return { ...state, stage: { ...state.stage, selected } };
        }

        // One press for a whole study. A study is the unit a clinician reasons about, so selecting
        // by it is what keeps a 30-series patient from being narrowed one checkbox at a time.
        case 'series-group-toggled': {
            if (state.stage.kind !== 'browsing') {
                return state;
            }
            const selected = new Set(state.stage.selected);
            for (const key of action.keys) {
                if (action.selected) {
                    selected.add(key);
                } else {
                    selected.delete(key);
                }
            }
            return { ...state, stage: { ...state.stage, selected } };
        }

        // Only a source that listed the series can be chosen for it.
        case 'series-source-chosen': {
            if (state.stage.kind !== 'browsing') {
                return state;
            }
            const row = state.stage.rows.find((r) => r.key === action.key);
            if (!row || !row.sources.includes(action.source)) {
                return state;
            }
            const retrieveFrom = new Map(state.stage.retrieveFrom);
            retrieveFrom.set(action.key, action.source);
            return { ...state, stage: { ...state.stage, retrieveFrom } };
        }

        case 'request-started': {
            if (
                state.stage.kind !== 'browsing' ||
                state.stage.selected.size === 0
            ) {
                return state;
            }
            return {
                ...state,
                generation: action.generation,
                error: undefined,
                stage: {
                    kind: 'requesting',
                    ...pickedOf(state.stage),
                    rows: state.stage.rows,
                    selected: state.stage.selected,
                    retrieveFrom: state.stage.retrieveFrom,
                    accepted: 0,
                    refused: 0,
                    unanswered: 0,
                    notRequested: 0,
                    total: state.stage.selected.size,
                },
            };
        }

        case 'request-progress': {
            if (action.generation !== state.generation) {
                return state;
            }
            if (state.stage.kind !== 'requesting') {
                return state;
            }
            return {
                ...state,
                stage: {
                    ...state.stage,
                    accepted: action.accepted,
                    refused: action.refused,
                    unanswered: action.unanswered,
                    notRequested: action.notRequested,
                },
            };
        }

        case 'request-settled': {
            if (action.generation !== state.generation) {
                return state;
            }
            if (state.stage.kind !== 'requesting') {
                return state;
            }

            // Nothing was accepted, so there is no arrival to follow. Following one here would
            // count instances against a total no answer ever established.
            if (action.accepted.length === 0) {
                return {
                    ...state,
                    stage: {
                        kind: 'refused',
                        ...pickedOf(state.stage),
                        refused: action.refused,
                        unanswered: action.unanswered,
                        notRequested: action.notRequested,
                    },
                };
            }

            return {
                ...state,
                stage: {
                    kind: 'arriving',
                    ...pickedOf(state.stage),
                    refused: action.refused,
                    unanswered: action.unanswered,
                    notRequested: action.notRequested,
                    arrivals: initialArrivals(action.accepted),
                },
            };
        }

        // One observation of the connector's record. The stage advances to terminal only when every
        // accepted series has arrived or failed, so a series still in flight keeps the whole report
        // open rather than letting a partial retrieval read as a finished one.
        case 'arrivals-observed': {
            if (action.generation !== state.generation) {
                return state;
            }
            if (state.stage.kind !== 'arriving') {
                return state;
            }
            if (!summariseArrivals(action.arrivals).settled) {
                return {
                    ...state,
                    stage: { ...state.stage, arrivals: action.arrivals },
                };
            }
            return {
                ...state,
                stage: {
                    kind: 'arrived',
                    ...pickedOf(state.stage),
                    refused: state.stage.refused,
                    unanswered: state.stage.unanswered,
                    notRequested: state.stage.notRequested,
                    arrivals: action.arrivals,
                    patientId: action.patientId,
                },
            };
        }

        case 'reset':
            // The included sources survive a reset so the next lookup asks the same sources. The
            // term and the lookup are patient identifiers and never survive the screen that used
            // them: on a shared workstation a kept term would resume one operator's search for the
            // next.
            return initialPacsImportState(action.generation, state.sources);
    }
}
